import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, where, orderBy, startAfter } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';
import {
  ChartBarIcon,
  UsersIcon,
  UserGroupIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const OrganizationMetrics = () => {
  const { isSuperAdmin } = useAuth();
  const [metrics, setMetrics] = useState({
    totalOrgs: 0,
    activeOrgs: 0,
    pendingOrgs: 0,
    totalMembers: 0,
    totalGroups: 0,
    membershipTrend: {
      lastWeek: 0,
      lastMonth: 0,
      lastQuarter: 0
    },
    orgStats: {
      averageMemberCount: 0,
      averageGroupCount: 0,
      mostActiveOrg: null,
      newestOrg: null
    },
    totalStories: 0,
    approvedStories: 0,
    activeMembers: 0,
    newMembersThisMonth: 0,
    createdAt: null,
    lastActivityAt: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMetrics = async () => {
      if (!isSuperAdmin) return;
      
      try {
        const now = new Date();
        const oneWeekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
        const oneMonthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
        const oneQuarterAgo = new Date(now - 90 * 24 * 60 * 60 * 1000);

        // Fetch all organizations
        const orgsRef = collection(db, 'organizations');
        const [approvedSnap, pendingSnap] = await Promise.all([
          getDocs(query(orgsRef, where('status', '==', 'approved'))),
          getDocs(query(orgsRef, where('status', '==', 'pending')))
        ]);

        let totalMembers = 0;
        let totalGroups = 0;
        let activeOrgs = 0;
        let mostActiveOrg = null;
        let newestOrg = null;
        let maxActivity = 0;
        let newMembersLastWeek = 0;
        let newMembersLastMonth = 0;
        let newMembersLastQuarter = 0;
        
        // Process organizations
        await Promise.all(approvedSnap.docs.map(async (orgDoc) => {
          const orgData = orgDoc.data();
          const orgId = orgDoc.id;
          
          // Count active members (source of truth: members subcollection)
          const membersRef = collection(db, 'organizations', orgId, 'members');
          const membersSnap = await getDocs(membersRef);
          const activeMembers = membersSnap.docs.filter(d => d.data().status === 'active');
          totalMembers += activeMembers.length;

          // Count groups
          totalGroups += orgData.groupCount || 0;

          // Check if org is active
          if (orgData.lastActivityAt?.toDate() > oneMonthAgo) {
            activeOrgs++;
          }

          // Count new members in different time periods
          activeMembers.forEach(doc => {
            const joinDate = doc.data().joinedAt?.toDate();
            if (!joinDate) return;
            
            if (joinDate >= oneWeekAgo) {
              newMembersLastWeek++;
            }
            if (joinDate >= oneMonthAgo) {
              newMembersLastMonth++;
            }
            if (joinDate >= oneQuarterAgo) {
              newMembersLastQuarter++;
            }
          });

          // Track most active org
          const activeUsersThisWeek = orgData.metrics?.activeUsersThisWeek || 0;
          if (activeUsersThisWeek > maxActivity) {
            maxActivity = activeUsersThisWeek;
            mostActiveOrg = {
              id: orgId,
              name: orgData.name,
              activeMembers: activeUsersThisWeek
            };
          }

          // Track newest org
          if (!newestOrg || orgData.createdAt > newestOrg.createdAt) {
            newestOrg = {
              id: orgId,
              name: orgData.name,
              createdAt: orgData.createdAt
            };
          }
        }));

        setMetrics({
          totalOrgs: approvedSnap.size,
          pendingOrgs: pendingSnap.size,
          activeOrgs,
          totalMembers,
          totalGroups,
          membershipTrend: {
            lastWeek: newMembersLastWeek,
            lastMonth: newMembersLastMonth,
            lastQuarter: newMembersLastQuarter
          },
          orgStats: {
            averageMemberCount: Math.round(totalMembers / approvedSnap.size),
            averageGroupCount: Math.round(totalGroups / approvedSnap.size),
            mostActiveOrg,
            newestOrg
          },
          totalStories: 0,
          approvedStories: 0,
          activeMembers: 0,
          newMembersThisMonth: 0,
          createdAt: newestOrg.createdAt,
          lastActivityAt: mostActiveOrg.activeMembers > 0 ? new Date() : null
        });
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching metrics:', error);
        setIsLoading(false);
      }
    };

    fetchMetrics();
  }, [isSuperAdmin]);

  if (!isSuperAdmin) {
    return (
      <div className="p-6">
        <p className="text-red-600">You don't have permission to view this page.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <p>Loading metrics...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Add navigation header */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/organizations')}
            className="text-gray-600 hover:text-gray-800"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Organization Metrics</h1>
        </div>
        <div className="flex space-x-4">
          <Link
            to="/organizations"
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            View All Organizations
          </Link>
          <Link
            to="/app-admin"
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Back to Admin
          </Link>
        </div>
      </div>

      <div className="mb-8">
        <p className="text-gray-600">Overview of all organizations and their activity</p>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Organizations</p>
              <p className="text-2xl font-semibold text-gray-900">{metrics.totalOrgs}</p>
            </div>
            <ChartBarIcon className="h-8 w-8 text-blue-500" />
          </div>
          <p className="mt-2 text-sm text-gray-600">{metrics.activeOrgs} active in last 30 days</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Members</p>
              <p className="text-2xl font-semibold text-gray-900">{metrics.totalMembers}</p>
            </div>
            <UsersIcon className="h-8 w-8 text-green-500" />
          </div>
          <p className="mt-2 text-sm text-gray-600">Across all organizations</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Groups</p>
              <p className="text-2xl font-semibold text-gray-900">{metrics.totalGroups}</p>
            </div>
            <UserGroupIcon className="h-8 w-8 text-purple-500" />
          </div>
          <p className="mt-2 text-sm text-gray-600">Active study groups</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Member Growth</p>
              <p className="text-2xl font-semibold text-gray-900">+{metrics.membershipTrend.lastMonth}</p>
            </div>
            <CalendarIcon className="h-8 w-8 text-orange-500" />
          </div>
          <p className="mt-2 text-sm text-gray-600">New members this month</p>
        </div>
      </div>

      {/* Growth Trends */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Membership Growth</h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-sm text-gray-600">Last Week</p>
            <p className="text-xl font-semibold text-gray-900">+{metrics.membershipTrend.lastWeek}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Last Month</p>
            <p className="text-xl font-semibold text-gray-900">+{metrics.membershipTrend.lastMonth}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Last Quarter</p>
            <p className="text-xl font-semibold text-gray-900">+{metrics.membershipTrend.lastQuarter}</p>
          </div>
        </div>
      </div>

      {/* Story Metrics */}
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold">Stories</h3>
        <div className="mt-2 space-y-1">
          <p>Total Stories: {metrics.totalStories}</p>
          <p>Approved Stories: {metrics.approvedStories}</p>
          <p>Pending Stories: {metrics.totalStories - metrics.approvedStories}</p>
        </div>
      </div>
      
      {/* Member Activity */}
      <div className="bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold">Member Activity</h3>
        <div className="mt-2 space-y-1">
          <p>Active Members: {metrics.activeMembers}</p>
          <p>Total Members: {metrics.totalMembers}</p>
          <p>New This Month: {metrics.newMembersThisMonth}</p>
        </div>
      </div>

      <div className="text-sm">
        <p>Organization Created: {metrics.createdAt?.toDate()?.toLocaleDateString() || 'N/A'}</p>
        <p>Last Activity: {metrics.lastActivityAt?.toDate()?.toLocaleDateString() || 'N/A'}</p>
      </div>

      <p>Last Updated: {
        metrics.lastActivityAt?.toDate()?.toLocaleString() || 'N/A'
      }</p>
    </div>
  );
};

export default OrganizationMetrics; 